import { useMemo, useState } from "react";
import ResultModal from "./ResultModal.jsx";
import config from "../config/index.js";
import { useWallet } from "../context/WalletContext.jsx";
import { getContract } from "../services/contracts.js";
import { getTxOverrides } from "../services/gas.js";
import { decodeContractError } from "../utils/contractError.js";
import { formatUsdc, liveAccruedInterest, parseUsdc } from "../utils/format.js";

// Matches the contract's MIN_INTEREST_WITHDRAWAL ($10 USDC, 6dp).
export const MIN_INTEREST_WITHDRAWAL = 10_000_000n;

/**
 * Shared "withdraw accrued interest" flow: the claimable-amount maths, the
 * button, the amount modal and the result modal.
 *
 * §M6 (Jul 2026 client review) asked for the Withdraw button on the My Term
 * Deposits "Net Yield" card as well as the Dashboard. Extracting it here
 * means the two surfaces can't drift — in particular they can't disagree
 * about how much is actually claimable, which is what produced the
 * "$82 expected vs $31 offered" report in the first place.
 *
 * CLAIMABLE AMOUNT — the important part:
 *   `BondContract._withdrawInterestFor` runs `_netAgainstOpenLoan` BEFORE it
 *   sizes the payout (the §C2 self-paying-netting defence), so a deposit's
 *   bond yield services its own loan first and only the surplus can leave.
 *   Quoting gross accrual made every "Max" withdrawal revert with
 *   InsufficientAccruedInterest. We therefore use the DAY-STEPPED accrual
 *   (what the contract credits) minus the loan's accrued interest, per
 *   deposit, floored at zero.
 *
 * @param deposits  Active deposits (state === 1) for the connected wallet.
 * @param nowSec    Chain time in seconds — never wall clock.
 * @param onDone    Called after a successful withdrawal so the caller can refresh.
 */
export function useWithdrawInterest({ deposits, nowSec, onDone }) {
    const { signer } = useWallet();
    const [open, setOpen] = useState(false);
    const [amount, setAmount] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const active = useMemo(
        () => (deposits || []).filter((d) => Number(d.state) === 1),
        [deposits]
    );

    const { total, ids, reserved, accrued } = useMemo(() => {
        let claimable = 0n;
        let reservedTotal = 0n;
        let accruedTotal = 0n;
        const idList = [];
        for (const d of active) {
            const pending = liveAccruedInterest(
                d.principal,
                d.startTime,
                d.maturityTime,
                nowSec,
                Number(d.annualRateBps) || config.protocol.annualRateBps,
                d.realizedInterest
            );
            const loanInterest = BigInt(d.loanAccruedInterest || 0);
            const netted = pending < loanInterest ? pending : loanInterest;
            const surplus = pending - netted;
            accruedTotal += pending;
            reservedTotal += netted;
            if (surplus > 0n) {
                claimable += surplus;
                idList.push(Number(d.id));
            }
        }
        return { total: claimable, ids: idList, reserved: reservedTotal, accrued: accruedTotal };
    }, [active, nowSec]);

    const canWithdraw = Boolean(signer) && total >= MIN_INTEREST_WITHDRAWAL;
    const belowMin = Boolean(signer) && total < MIN_INTEREST_WITHDRAWAL;

    function openModal() {
        setAmount(formatUsdc(total));
        setResult(null);
        setOpen(true);
    }

    async function confirm() {
        let amountWei;
        try {
            amountWei = parseUsdc(amount);
        } catch {
            amountWei = 0n;
        }
        if (amountWei < MIN_INTEREST_WITHDRAWAL) {
            setResult({
                type: "error",
                title: "Amount too low",
                message: "Minimum withdrawal is $10 USDC."
            });
            return;
        }
        if (amountWei > total) {
            setResult({
                type: "error",
                title: "Amount too high",
                message: `You have $${formatUsdc(total)} of claimable interest available.`
            });
            return;
        }
        setBusy(true);
        try {
            const bond = getContract("bond", signer);
            const overrides = await getTxOverrides(signer.provider);
            const tx = await bond.withdrawInterestAmount(ids, amountWei, overrides);
            await tx.wait();
            await onDone?.();
            setOpen(false);
            setResult({
                type: "success",
                title: "Interest Withdrawn",
                message: `$${formatUsdc(amountWei)} USDC has been sent to your wallet. Your deposits keep earning.`
            });
        } catch (err) {
            setResult({ type: "error", title: "Withdrawal Failed", message: decodeContractError(err) });
        } finally {
            setBusy(false);
        }
    }

    const button = (
        <button
            type="button"
            disabled={!canWithdraw}
            onClick={openModal}
            title={
                canWithdraw
                    ? "Withdraw claimable interest to your wallet"
                    : belowMin
                    ? `Claimable interest must reach $10 to withdraw (you have $${formatUsdc(total)})`
                    : "Connect your wallet to withdraw"
            }
            className="inline-flex items-center gap-1 rounded-full bg-[#FBC206] px-2.5 py-1 font-manrope text-[10.5px] font-bold text-[#10281b] transition-all hover:-translate-y-0.5 hover:shadow-md disabled:cursor-not-allowed disabled:bg-white/10 disabled:text-white/40 disabled:translate-y-0 disabled:shadow-none disabled:hover:translate-y-0"
        >
            {/* Short label — the full explanation is in the `title` above.
                "Min $10 to withdraw" is wide enough to squeeze the figure off
                a quarter-width stat card. */}
            {belowMin ? "Min $10" : "Withdraw"}
            <svg
                className="h-3 w-3"
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
            >
                <polyline points="7 4 13 10 7 16" />
            </svg>
        </button>
    );

    const modals = (
        <>
            <WithdrawInterestModal
                open={open}
                available={total}
                accrued={accrued}
                reservedForLoans={reserved}
                amount={amount}
                setAmount={setAmount}
                busy={busy}
                onConfirm={confirm}
                onClose={() => !busy && setOpen(false)}
            />
            <ResultModal
                open={result !== null && !open}
                type={result?.type}
                title={result?.title || ""}
                message={result?.message || ""}
                onClose={() => setResult(null)}
            />
        </>
    );

    return { claimable: total, reserved, accrued, canWithdraw, belowMin, button, modals };
}

/**
 * Amount entry for a partial/full interest withdrawal, with a breakdown
 * reconciling total accrued yield against what is claimable right now.
 */
export function WithdrawInterestModal({
    open,
    available,
    accrued = 0n,
    reservedForLoans = 0n,
    amount,
    setAmount,
    busy,
    onConfirm,
    onClose
}) {
    if (!open) return null;
    let amountWei;
    try {
        amountWei = parseUsdc(amount || "0");
    } catch {
        amountWei = 0n;
    }
    const tooLow = amountWei < MIN_INTEREST_WITHDRAWAL;
    const tooHigh = amountWei > available;
    // Accrued but not yet claimable for the OTHER reason: the contract
    // credits interest per whole elapsed day, so today's part-day is not
    // withdrawable yet.
    const notYetCredited =
        accrued > available + reservedForLoans ? accrued - available - reservedForLoans : 0n;

    return (
        <div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 px-4"
            role="dialog"
            aria-modal="true"
        >
            <div className="w-full max-w-sm rounded-[14px] bg-[#10281b] ring-1 ring-[#bfb67b] p-5 text-white">
                <h3 className="font-manrope text-[15px] font-bold text-[#e6c985]">Withdraw Interest</h3>
                <p className="mt-1 text-[11px] text-[#e6c985]/70">
                    Available: <span className="font-semibold text-white">${formatUsdc(available)} USDC</span>.
                    Your deposits keep earning.
                </p>
                <div className="mt-3 flex items-center gap-2 rounded-[10px] bg-[#162e1f] ring-1 ring-[#bfb67b] px-3 py-2">
                    <span className="font-manrope text-[18px] font-bold text-white/45">$</span>
                    <input
                        type="number"
                        min="10"
                        step="1"
                        autoFocus
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="flex-1 min-w-0 bg-transparent font-manrope text-[20px] font-bold text-white placeholder:text-white/30 focus:outline-none [color-scheme:dark]"
                        placeholder="0"
                    />
                    <button
                        type="button"
                        onClick={() => setAmount(formatUsdc(available))}
                        className="rounded-md bg-white/10 px-2 py-0.5 text-[10px] font-semibold text-[#e6c985] hover:bg-white/20"
                    >
                        Max
                    </button>
                </div>
                <p className="mt-1.5 text-[10px] text-[#e6c985]/70">Minimum withdrawal is $10 USDC.</p>
                {reservedForLoans > 0n || notYetCredited > 0n ? (
                    <div className="mt-2 space-y-1 rounded-[8px] bg-[#162e1f] px-3 py-2 text-[10px] text-[#e6c985]/75">
                        <div className="flex items-center justify-between">
                            <span>Total accrued yield</span>
                            <span className="font-semibold text-white">${formatUsdc(accrued)}</span>
                        </div>
                        {reservedForLoans > 0n ? (
                            <div className="flex items-center justify-between">
                                <span>− Servicing your open loans</span>
                                <span className="font-semibold text-white">${formatUsdc(reservedForLoans)}</span>
                            </div>
                        ) : null}
                        {notYetCredited > 0n ? (
                            <div className="flex items-center justify-between">
                                <span>− Accrued today, credited at day end</span>
                                <span className="font-semibold text-white">${formatUsdc(notYetCredited)}</span>
                            </div>
                        ) : null}
                        <div className="flex items-center justify-between border-t border-white/10 pt-1">
                            <span className="font-semibold text-[#e6c985]">= Available now</span>
                            <span className="font-bold text-[#FBC206]">${formatUsdc(available)}</span>
                        </div>
                    </div>
                ) : null}
                {tooHigh ? (
                    <p className="mt-1 text-[10px] font-medium text-[#C93030]">
                        You only have ${formatUsdc(available)} available.
                    </p>
                ) : null}
                <div className="mt-4 flex gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={busy}
                        className="flex-1 rounded-md bg-white/10 px-4 py-2 text-[12px] font-semibold text-[#e6c985] hover:bg-white/20 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={busy || tooLow || tooHigh}
                        className="flex-1 rounded-md bg-[#FBC206] px-4 py-2 text-[12px] font-bold text-[#10281b] transition-all hover:-translate-y-0.5 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-40 disabled:translate-y-0 disabled:hover:shadow-none"
                    >
                        {busy ? "Withdrawing…" : "Withdraw"}
                    </button>
                </div>
            </div>
        </div>
    );
}
